import axios, { CancelTokenSource } from 'axios'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { IQuizQuestion, IQuizResponse } from '../../models/WorkplaceQuiz'
import WorkplaceQuizService from '../../services/WorkplaceQuizService'
import { isEmpty, onKeyPressHandler } from '../../utilities/commonFunctions'
import Conditional from '../common/Conditional'
import WorkplaceQuizContext from './context/WorkplaceQuizContext'
import WorkplaceQuizModuleIntro from './WorkplaceQuizModuleIntro'
import WorkplaceQuizModuleQuestion from './WorkplaceQuizModuleQuestion'
import WorkplaceQuizProgressbar from './WorkplaceQuizProgressbar'
import WorkplaceQuizModuleFinished from './WorkplaceQuizModuleFinished'
import WorkplaceGlossaryModal, {
  IWorkplaceGlossaryModalProps,
} from './modals/WorkplaceGlossaryModal'
import WorkplaceQuizModuleMenu from './WorkplaceQuizModuleMenu'

export interface IWorkplaceQuizModuleProps {
  quizId: number
  isOpen: boolean
  handleErrors?: (any) => void
  startQuiz: (id: number) => void
  viewResults: () => void
  menuToggle: () => void
}

const WorkplaceQuizModule = (props: IWorkplaceQuizModuleProps) => {
  const mountedRef = useRef(false)
  const context = useContext(WorkplaceQuizContext)
  const [isLoading, setIsLoading] = useState(true)
  const { getQuizModule } = WorkplaceQuizService
  const [quiz, setQuiz] = useState<IQuizResponse>()
  const [counter, setCounter] = useState(0)
  const [showStep, setShowStep] = useState({
    completed: false,
    intro: false,
    questions: false,
  })
  const [showGlossaryModal, setGlossaryModal] =
    useState<IWorkplaceGlossaryModalProps>({
      show: false,
      title: '',
      content: '',
      onConfirm: () => {
        setGlossaryModal((prevState) => ({
          ...prevState,
          show: false,
          title: '',
          content: '',
        }))
      },
    })
  const getModule = async (id: number, source: CancelTokenSource) => {
    return await getQuizModule(id, source).then((response) => {
      return response.result
    })
  }

  const handleGlossaryAnchorClick = (event) => {
    if (event?.keyCode != null && event?.keyCode != '13') {
      return
    }
    const attribute = event.target.attributes.getNamedItem(
      'data-tnt-glossary-term'
    )
    if (attribute && !isEmpty(attribute.value)) {
      const glossary = context.currentState.glossaries.find(
        (p) => p.name.toLowerCase() === attribute.value.toLowerCase()
      )
      if (glossary) {
        setGlossaryModal((prevState) => ({
          ...prevState,
          show: true,
          title: glossary.title,
          content: glossary.value,
        }))
      }
    }
  }

  useEffect(() => {
    mountedRef.current = true
    const source = axios.CancelToken.source()
    window.addEventListener('click', handleGlossaryAnchorClick)
    window.addEventListener('keyup', handleGlossaryAnchorClick)

    getModule(props.quizId, source)
      .then((response) => {
        if (mountedRef.current) {
          //Begin quiz from where it was last left.
          const lastQuestionId = context.getLastQuestion(props.quizId)
          if (
            response &&
            response.questions?.length > 0 &&
            (lastQuestionId !== null || lastQuestionId !== undefined)
          ) {
            const index = response.questions.findIndex(
              (q) => q.textId === lastQuestionId
            )
            if (index <= 0) {
              setCounter(0)
            } else {
              setCounter(index)
              response.resume = true
            }
          } else {
            setCounter(0)
          }
          const replaceText = '<a data-tnt-glossary-term'
          const replacementText =
            '<a class="workplace-tooltip" data-tooltip="Show definition" tabindex="0" data-tnt-glossary-term'
          response.introductionContent =
            response.introductionContent.replaceAll(
              replaceText,
              replacementText
            )

          response.questions.forEach((question) => {
            question.almostRightFeedbackContent =
              question.almostRightFeedbackContent.replaceAll(
                replaceText,
                replacementText
              )

            question.content = question.content.replaceAll(
              replaceText,
              replacementText
            )

            question.correctFeedbackContent =
              question.correctFeedbackContent.replaceAll(
                replaceText,
                replacementText
              )

            question.failureFeedbackContent =
              question.failureFeedbackContent.replaceAll(
                replaceText,
                replacementText
              )

            question.maybeFeedbackContent =
              question.maybeFeedbackContent.replaceAll(
                replaceText,
                replacementText
              )

            question.retryFeedbackContent =
              question.retryFeedbackContent.replaceAll(
                replaceText,
                replacementText
              )

            question.options.forEach((option) => {
              option.content = option.content.replaceAll(
                replaceText,
                replacementText
              )
            })
          })

          setQuiz(response)
          setShowStep((prevState) => ({
            ...prevState,
            intro: true,
            questions: false,
            completed: false,
          }))
        }
      })
      .catch((error) => {
        props.handleErrors(error)
      })
      .finally(() => {
        if (mountedRef.current) {
          setIsLoading(false)
          window.scrollTo(0, 900)
        }
      })

    return () => {
      mountedRef.current = false
      window.removeEventListener('click', handleGlossaryAnchorClick)

      if (source) {
        source.cancel('Operation cancelled')
      }
    }
  }, [props.quizId])

  const startQuiz = () => {
    setQuiz((prevState) => ({
      ...prevState,
      resume: counter > 0,
      currentQuestion: quiz.questions[counter],
    }))
    setShowStep((prevState) => ({
      ...prevState,
      intro: false,
      questions: true,
      completed: false,
    }))
  }

  const next = () => {
    const tmp = counter + 1
    if (tmp > quiz.questions.length - 1) {
      context.quizCompleted(quiz.id)
      setShowStep((prevState) => ({
        ...prevState,
        intro: false,
        questions: false,
        completed: true,
      }))

      return
    }
    setCounter(tmp)
    setQuiz((prevState) => ({
      ...prevState,
      currentQuestion: quiz.questions[counter + 1],
    }))
  }

  const back = () => {
    if (counter - 1 <= 0) {
      setCounter(0)
    } else {
      setCounter((counter) => counter - 1)
    }

    let currentQuestion: IQuizQuestion
    let showIntro: boolean

    if (counter - 1 >= 0) {
      currentQuestion = quiz.questions[counter - 1]
      showIntro = false
    } else {
      currentQuestion = null
      showIntro = true
    }

    setQuiz((prevState) => ({
      ...prevState,
      showIntro: showIntro,
      currentQuestion: currentQuestion,
    }))

    setShowStep((prevState) => ({
      ...prevState,
      intro: showIntro,
      questions: !showIntro,
      completed: false,
    }))
  }

  const completed = () => {
    context.quizCompleted(quiz.id)
    props.startQuiz(0) //This will show the home screen
    setShowStep((prevState) => ({
      ...prevState,
      intro: false,
      questions: false,
      completed: false,
    }))
  }

  const startAgain = () => {
    setCounter(0)
    setQuiz((prevState) => ({
      ...prevState,
      currentQuestion: quiz.questions[0],
    }))
    setShowStep((prevState) => ({
      ...prevState,
      intro: false,
      questions: true,
      completed: false,
    }))
  }

  const viewResult = () => {
    setShowStep((prevState) => ({
      ...prevState,
      intro: false,
      questions: false,
      completed: true,
    }))
  }

  const menuToggle = () => {
    props.menuToggle()
  }

  return (
    <Conditional if={!isLoading}>
       <WorkplaceQuizModuleMenu
        isOpen={props.isOpen}
        viewResult={props.viewResults}
        startQuiz={props.startQuiz}
      />
      <div className="contentAll open">
        <Conditional if={!isLoading && showStep.intro}>
          <div className="menu"></div>
          <div className={`${'intro'} ${quiz?.image}`}>
            <div className="barHeaderIntro">
              <div
                className="quickMenuArea"
                onClick={() => menuToggle()}
                role="button"
                tabIndex={0}
                onKeyUp={(event) => {
                  if (event?.keyCode == '13') {
                    menuToggle()
                  }
                }}
              >
                <a className="quickMenu">
                  <img
                    src="/public/workplace/assets/images/button/clear/quickmenu_normal.png"
                    alt="Quick Menu"
                  />
                  <span>Quick Menu - show/hide above.</span>
                </a>
              </div>
              <WorkplaceQuizModuleMenu
                isOpen={props.isOpen}
                viewResult={props.viewResults}
                startQuiz={props.startQuiz}
              />
              <div className="title">
                <h2 className="titleContent">
                  Workplace <span>Basics</span>
                </h2>
              </div>
              <WorkplaceQuizProgressbar
                title={quiz?.introTitleContent}
                current={counter}
                total={quiz?.questions.length}
                showNumber={false}
              />
            </div>
            <div className="content">
              <div className="contentArea">
                <WorkplaceQuizModuleIntro
                  quizResponse={quiz}
                  startQuiz={startQuiz}
                  quizStatus={
                    quiz && quiz.id > 0 ? context.getQuizState(quiz?.id) : null
                  }
                  startAgain={startAgain}
                  viewResult={viewResult}
                  handleErrors={props.handleErrors}
                />
              </div>
            </div>
          </div>
        </Conditional>
        <Conditional if={!isLoading && showStep.questions}>
          <WorkplaceQuizModuleQuestion
            quizId={props.quizId}
            introTitleContent={quiz?.introTitleContent}
            question={quiz?.currentQuestion}
            resources={quiz?.resources}
            next={next}
            back={back}
            index={counter}
            total={quiz?.questions.length}
            handleErrors={props.handleErrors}
            menuToggle={menuToggle}
          />
        </Conditional>
        <Conditional if={!isLoading && showStep.completed}>
          <div className={`summary ${quiz?.image}`}>
            <div className="barHeaderSummary">
              <div
                className="quickMenuArea"
                onClick={() => menuToggle()}
                role="button"
                tabIndex={0}
                onKeyPress={onKeyPressHandler}
              >
                <a className="quickMenu">
                  <img
                    src="/public/workplace/assets/images/button/clear/quickmenu_normal.png"
                    alt="Quick Menu"
                  />
                  <span>Quick Menu - show/hide above.</span>
                </a>
              </div>
              <div className="title">
                <h2 className="titleContent">
                  Workplace <span>Basics</span>
                </h2>
              </div>
              <WorkplaceQuizProgressbar
                title={quiz?.introTitleContent}
                current={counter}
                total={quiz?.questions.length}
                showNumber={false}
              />
            </div>
            <div className="content">
              <WorkplaceQuizModuleFinished
                quizId={props.quizId}
                summaryContent={quiz?.summaryContent}
                summaryAfterContent={quiz?.summaryAfterContent}
                dynamicContent={quiz?.dynamicContent}
                fixedContent={quiz?.fixedContent}
                resources={quiz?.resources}
                completed={completed}
                startAgain={startAgain}
              />
            </div>
          </div>
        </Conditional>
      </div>
      <WorkplaceGlossaryModal {...showGlossaryModal} />
    </Conditional>
  )
}

export default WorkplaceQuizModule
